import { useCallback, useEffect, useSyncExternalStore } from 'react';
import {
  THEME_EVENT,
  type ResolvedTheme,
  type ThemePref,
  currentResolved,
  initTheme,
  readThemePref,
  setThemePref,
} from './theme';

/**
 * Slice 23 (admin half) — React's window onto the theme controller. Ported from
 * the web app's `useTheme`.
 *
 * `useSyncExternalStore` over `THEME_EVENT`, so a toggle anywhere (or an `auto`
 * dusk flip) re-renders every subscribed component — no second source of truth.
 * The store value is the resolved palette; the preference is read alongside it.
 * (The admin portal styles its chrome entirely through CSS tokens, so unlike the
 * web app there is no map/canvas layer subscribing to the event to repaint — the
 * event exists here for the React subscription alone.)
 */
function subscribe(onChange: () => void): () => void {
  window.addEventListener(THEME_EVENT, onChange);
  return () => window.removeEventListener(THEME_EVENT, onChange);
}

export function useTheme(): {
  pref: ThemePref;
  resolved: ResolvedTheme;
  setPref: (p: ThemePref) => void;
} {
  // Start the live `auto` follow once (idempotent). Not in render — effects only.
  useEffect(() => {
    initTheme();
  }, []);

  const resolved = useSyncExternalStore(
    subscribe,
    () => currentResolved(),
    () => 'dark' as ResolvedTheme,
  );
  const pref = useSyncExternalStore(
    subscribe,
    () => readThemePref(),
    () => 'dark' as ThemePref,
  );

  const setPref = useCallback((p: ThemePref) => {
    setThemePref(p);
  }, []);

  return { pref, resolved, setPref };
}
